import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, Input, OnInit } from '@angular/core';
import {
  AntipatternStore,
  AppView,
  AssetsStore,
  StudentStore,
  UserStore,
  ViewStore,
  isCurrentUserNotRestricted,
} from '@coach-bot/data-access/shared';
import { AnalyticsEvent } from '@coach-bot/features/analytics';
import { getAssetsPath } from '@environment';
import { ExtensionCommunicationService } from '@shared/extension';
import { Language, initializeTranslations } from '@shared/translate';
import { AbstractComponent } from '@shared/ui';
import { ONE_SECOND_MS } from '@shared/utils/constants';
import { BehaviorSubject, Observable, Subscription, combineLatest, filter, map, take, takeUntil, timer } from 'rxjs';
import { TRANSLATIONS, TRANSLATIONS_VERSION } from '../../../../../translations';
import { AppSubscriptionsService } from '../../services';

enum AppState {
  Active = 'Active',
  Inactive = 'Inactive',
  Loading = 'Loading',
}

const UPDATE_IS_SERVICE_WORKER_ACTIVE_INTERVAL_MS = ONE_SECOND_MS * 5;

@Component({
  selector: 'da-extension',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent extends AbstractComponent implements OnInit {
  @Input() assets: string;

  currentState$: Observable<AppState>;
  currentView$: BehaviorSubject<AppView> = this.viewStore.currentView$;

  AppState = AppState;
  AppView = AppView;

  private isCurrentUserNotRestricted$ = new BehaviorSubject<boolean | null>(null);
  private initialized$: Observable<boolean>;
  private isServiceWorkerActive$ = new BehaviorSubject<boolean | null>(null);
  private storesInitialized$: Observable<boolean>;
  private translationsInitialized$: BehaviorSubject<boolean> = this.viewStore.translationsInitialized$;

  private isServiceWorkerActiveSubscription: Subscription | null = null;

  constructor(
    private appSubscriptionsService: AppSubscriptionsService,
    private antipatternStore: AntipatternStore,
    private assetsStore: AssetsStore,
    private changeDetectorRef: ChangeDetectorRef,
    private extensionCommunicationService: ExtensionCommunicationService,
    private injector: Injector,
    private studentStore: StudentStore,
    private userStore: UserStore,
    private viewStore: ViewStore
  ) {
    super();

    initializeTranslations(this.injector, getAssetsPath(), TRANSLATIONS_VERSION, TRANSLATIONS).then(
      (language: Language) => {
        this.viewStore.setCurrentLanguage(language);
        this.viewStore.setTranslationsInitialized(true);
      }
    );
  }

  ngOnInit(): void {
    this.assets = this.assets || getAssetsPath() || 'assets';
    this.assetsStore.setAssetsPath(this.assets);

    this.updateIsServiceWorkerActive().then(() => {
      this.subscribeToIsServiceWorkerActive();
    });

    this.storesInitialized$ = combineLatest([
      this.antipatternStore.initialized$,
      this.assetsStore.initialized$,
      this.studentStore.initialized$,
      this.userStore.initialized$,
      this.viewStore.initialized$,
    ]).pipe(
      takeUntil(this.destroyed$),
      map((storesInitialized: boolean[]) => storesInitialized.every((item: boolean) => item))
    );

    this.storesInitialized$.pipe(take(1)).subscribe(async () => {
      const isCurrentUserNotRestrictedValue: boolean = await isCurrentUserNotRestricted(
        this.extensionCommunicationService,
        this.userStore
      );
      this.isCurrentUserNotRestricted$.next(isCurrentUserNotRestrictedValue);
      if (isCurrentUserNotRestrictedValue) {
        if (!this.viewStore.currentView$.value) {
          this.viewStore.setView(AppView.SignIn);
        }
      } else {
        this.viewStore.setView(AppView.Empty);
      }
    });

    this.initialized$ = combineLatest([
      this.isCurrentUserNotRestricted$,
      this.isServiceWorkerActive$,
      this.storesInitialized$,
      this.translationsInitialized$,
    ]).pipe(
      map(
        ([isCurrentUserNotRestricted, isServiceWorkerActive, storesInitialized, translationsInitialized]: [
          boolean | null,
          boolean | null,
          boolean,
          boolean,
        ]) =>
          isCurrentUserNotRestricted !== null &&
          isServiceWorkerActive !== null &&
          storesInitialized &&
          translationsInitialized
      ),
      takeUntil(this.destroyed$)
    );

    this.currentState$ = combineLatest([this.currentView$, this.initialized$, this.isServiceWorkerActive$]).pipe(
      takeUntil(this.destroyed$),
      map(([currentView, initialized, isServiceWorkerActive]: [AppView, boolean, boolean]) => {
        if (!isServiceWorkerActive && isServiceWorkerActive !== null) {
          return AppState.Inactive;
        } else if (!!currentView && initialized && isServiceWorkerActive) {
          return AppState.Active;
        } else {
          return AppState.Loading;
        }
      })
    );

    combineLatest([this.currentState$, this.currentView$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.changeDetectorRef.detectChanges();
      });

    this.extensionCommunicationService.updateIsExtensionPopupOpened(true);
  }

  ngAfterViewInit(): void {
    this.storesInitialized$
      .pipe(
        filter((initialized: boolean) => initialized),
        take(1)
      )
      .subscribe(() => {
        this.appSubscriptionsService.subscribe();
        this.extensionCommunicationService.trackAnalyticsEvent(AnalyticsEvent.ExtensionIconClick);
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.appSubscriptionsService.unsubscribe();
  }

  private subscribeToIsServiceWorkerActive(): void {
    if (this.isServiceWorkerActiveSubscription) {
      this.isServiceWorkerActiveSubscription.unsubscribe();
    }
    this.isServiceWorkerActiveSubscription = timer(UPDATE_IS_SERVICE_WORKER_ACTIVE_INTERVAL_MS)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async () => {
        await this.updateIsServiceWorkerActive();
        this.subscribeToIsServiceWorkerActive();
      });
  }

  private async updateIsServiceWorkerActive(): Promise<void> {
    const value: boolean = await this.extensionCommunicationService.isServiceWorkerActive();
    if (value !== this.isServiceWorkerActive$.value) {
      this.isServiceWorkerActive$.next(value);
    }
  }
}
